import {
  isRbacV2ActionAllowed,
  isRbacV2ScopeAllowed,
  type RbacV2Resource,
  type RbacV2RolePermission,
} from './rbac.v2.js'
import { type HrPopulation, parseHrPopulation } from './rbac.population.js'
import { hasOwn } from './internal/hasOwn.js'
import type { StoreProjectApiStatus } from './store-project-fields.js'

const READ = ['read'] as const
const READ_EDIT = ['read', 'edit'] as const
export type RbacPermission = (typeof READ)[number] | (typeof READ_EDIT)[number]
export const rbacPermissions = [...new Set([...READ, ...READ_EDIT])] as const

const GLOBAL = ['global'] as const
const REAL_ESTATE_RESTRICTED = ['real-estate-restricted'] as const
const GLOBAL_STORE = ['global', 'store'] as const
export type RbacScope =
  | (typeof GLOBAL)[number]
  | (typeof GLOBAL_STORE)[number]
  | (typeof REAL_ESTATE_RESTRICTED)[number]
export const rbacScopes = [
  ...new Set([...GLOBAL, ...GLOBAL_STORE, ...REAL_ESTATE_RESTRICTED]),
] as const

interface RbacConditionFieldTypes {
  status: StoreProjectApiStatus
}

type RbacResourceDetailsInterface = Record<
  string,
  {
    actions: readonly RbacPermission[]
    scopes: readonly RbacScope[]
    conditionField?: keyof RbacConditionFieldTypes
    /**
     * Périmètre qu'applique le serveur à une ligne `role_permissions` stockée à `NULL`. Absent ⇒
     * `global`, seul périmètre des ressources mono-portée. Une ressource à plusieurs périmètres le
     * déclare explicitement : le serveur n'ouvre tout le réseau que sur un `global` explicite
     * (`getRealEstateV1WriteScope`), donc `NULL` y vaut le périmètre restreint.
     */
    nullScope?: RbacScope
  }
>

export const rbacResourceDetails = {
  dashboard: { actions: READ, scopes: GLOBAL },
  today: { actions: READ, scopes: GLOBAL },
  carts: { actions: READ, scopes: GLOBAL },
  categories: { actions: READ_EDIT, scopes: GLOBAL },
  products: { actions: READ_EDIT, scopes: GLOBAL },
  users: { actions: READ_EDIT, scopes: GLOBAL },
  promos: { actions: READ_EDIT, scopes: GLOBAL },
  'work-concil': { actions: READ_EDIT, scopes: GLOBAL },
  notifications: { actions: READ_EDIT, scopes: GLOBAL },
  stores: { actions: READ_EDIT, scopes: GLOBAL },
  accounting: { actions: READ_EDIT, scopes: GLOBAL },
  quality: { actions: READ_EDIT, scopes: GLOBAL },
  'cart-control': { actions: READ_EDIT, scopes: GLOBAL },
  'daily-sales': { actions: READ_EDIT, scopes: GLOBAL },
  sellers: { actions: READ_EDIT, scopes: GLOBAL },
  cameras: { actions: READ_EDIT, scopes: GLOBAL },
  'camera-views': { actions: READ_EDIT, scopes: GLOBAL },
  sensors: { actions: READ_EDIT, scopes: GLOBAL },
  'hr-planning': { actions: READ_EDIT, scopes: GLOBAL },
  'real-estate': {
    actions: READ_EDIT,
    scopes: [...GLOBAL, ...REAL_ESTATE_RESTRICTED],
    conditionField: 'status' as const,
    nullScope: 'real-estate-restricted' as const,
  },
  roles: { actions: READ_EDIT, scopes: GLOBAL },
  'platform-stocks': { actions: READ_EDIT, scopes: GLOBAL },
  'visio-reviews': { actions: READ_EDIT, scopes: GLOBAL },
} as const satisfies RbacResourceDetailsInterface

export type RbacResourceDetails = typeof rbacResourceDetails

export type ReadableRbacResource = {
  [K in RbacResource]: 'read' extends RbacResourceDetails[K]['actions'][number] ? K : never
}[RbacResource]

export const rbacResources = Object.keys(rbacResourceDetails) as (keyof RbacResourceDetails)[]
export type RbacResource = (typeof rbacResources)[number]

/**
 * Union of [action, resource] tuples derived from rbacResourceDetails.
 * Use this to type MongoAbility — TypeScript then enforces per-resource action constraints,
 * e.g. ability.can('edit', 'dashboard') is a compile error since dashboard only allows 'read'.
 *
 * For resources with a conditionField (e.g. 'real-estate' → 'status'), the subject can also
 * be an object carrying that field so CASL evaluates MongoDB-style conditions correctly.
 */
export type ResourceAbilityTuple = {
  [K in RbacResource]: (typeof rbacResourceDetails)[K] extends {
    conditionField: infer F extends keyof RbacConditionFieldTypes
  }
    ? [
        (typeof rbacResourceDetails)[K]['actions'][number],
        K | { [P in F]: RbacConditionFieldTypes[P] },
      ]
    : [(typeof rbacResourceDetails)[K]['actions'][number], K]
}[RbacResource]

export type PermissionKey = {
  [K in keyof RbacResourceDetails]: `${K}:${RbacResourceDetails[K]['actions'][number]}`
}[keyof RbacResourceDetails]

export type ParsedPermissionKey<K extends PermissionKey = PermissionKey> =
  K extends `${infer R}:${infer P}`
    ? R extends RbacResource
      ? P extends RbacPermission
        ? { resource: R; permissionType: P }
        : never
      : never
    : never

export interface RbacRolePermission {
  resource: RbacResource
  permissionType: RbacPermission
  scope: RbacScope
}

export interface CurrentAdminRbacPermissionGrant extends RbacRolePermission {
  permissionKey: PermissionKey
  /** Provenance conservée quand elle est disponible ; optionnelle pendant la transition. */
  roleId?: number
  /** Provenance conservée quand elle est disponible ; optionnelle pendant la transition. */
  permissionId?: number
}

/**
 * Grant v2 tel qu'il est résolu pour l'acteur courant : la permission, la population qu'elle
 * couvre, et le périmètre boutique de l'affectation qui l'a produite. Un grant est indissociable :
 * on ne croise jamais l'action de l'un avec le périmètre d'un autre.
 */
export interface CurrentAdminRbacV2Grant extends RbacV2RolePermission {
  /** Périmètre de l'affectation : `all` ou la liste explicite des boutiques. */
  storeIds?: 'all' | number[]
  /** Population couverte ; `null` ou absent vaut tous les postes et tous les types de contrat. */
  population?: HrPopulation | null
  /** Affectation d'origine — deux affectations du même rôle produisent deux grants distincts. */
  adminRoleId?: number
}

export interface CurrentAdminRbacPermissionsResponse {
  schemaVersion: 2
  authorizationModel: 'rbac-v2'
  enforcementEnabled: boolean
  grants: CurrentAdminRbacPermissionGrant[]
  /**
   * Champ de compatibilité temporaire. Le BO v2 seul le reçoit toujours à `true` et ne le consulte
   * plus pour prendre une décision.
   */
  permissionV2Enabled: boolean
  /** Grants v2 (taxonomie `rbac.v2.ts`) — seule source d'autorisation du BO. */
  grantsV2: CurrentAdminRbacV2Grant[]
}

const hasExactKeys = (
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[] = [],
): boolean => {
  const allowed = new Set([...required, ...optional])
  return (
    required.every((key) => hasOwn(value, key)) &&
    Object.keys(value).every((key) => allowed.has(key))
  )
}

const isOptionalPositiveInteger = (value: unknown): boolean =>
  value === undefined || (Number.isInteger(value) && Number(value) > 0)

/**
 * Périmètre effectif d'un grant v1 tel que le serveur l'applique : la valeur stockée quand elle
 * existe, sinon le `nullScope` de la ressource (`global` par défaut). C'est la seule définition de
 * ce que vaut un `NULL` en base — l'ability serveur (`getAdminGrants`), le périmètre d'écriture
 * foncier et le contrat `GET /rbac/me/permissions` la partagent, pour que le BO reçoive exactement
 * le périmètre que le serveur enforce : `edit real-estate` à `NULL` est « Prez projet », pas tout
 * le réseau (revue du 26/09/2026).
 */
export const resolveRbacV1Scope = (resource: RbacResource, scope: RbacScope | null): RbacScope => {
  if (scope !== null) return scope
  const detail: RbacResourceDetailsInterface[string] = rbacResourceDetails[resource]
  return detail.nullScope ?? 'global'
}

/**
 * Un grant v1 est connu quand son triplet (ressource, action, périmètre) existe dans le catalogue.
 * Une ligne `role_permissions` survit au retrait de sa ressource du code (`prices`, 09/07/2026) :
 * elle ne confère aucun droit, le serveur l'écarte de `GET /rbac/me/permissions` et le parseur du
 * back-office l'ignore sans rejeter le contrat.
 *
 * Test d'appartenance seulement : un périmètre `NULL` est admis là où son périmètre effectif
 * (`resolveRbacV1Scope`) l'est.
 */
export const isKnownRbacV1Grant = (grant: {
  resource: string
  permissionType: string
  scope: string | null
}): boolean => {
  if (!hasOwn(rbacResourceDetails, grant.resource)) return false
  const resource = grant.resource as RbacResource
  const detail = rbacResourceDetails[resource]
  const scope = grant.scope ?? resolveRbacV1Scope(resource, null)
  return (
    (detail.actions as readonly string[]).includes(grant.permissionType) &&
    (detail.scopes as readonly string[]).includes(scope)
  )
}

/** Forme d'un grant hérité : clés exactes, types, et clé de permission cohérente avec le couple. */
const isLegacyGrantShape = (value: unknown): value is CurrentAdminRbacPermissionGrant => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const grant = value as Record<string, unknown>
  return (
    hasExactKeys(
      grant,
      ['permissionKey', 'resource', 'permissionType', 'scope'],
      ['roleId', 'permissionId'],
    ) &&
    typeof grant.resource === 'string' &&
    typeof grant.permissionType === 'string' &&
    typeof grant.scope === 'string' &&
    typeof grant.permissionKey === 'string' &&
    isOptionalPositiveInteger(grant.roleId) &&
    isOptionalPositiveInteger(grant.permissionId) &&
    grant.permissionKey === `${grant.resource}:${grant.permissionType}`
  )
}

/** Périmètre d'affectation porté par un grant : `all` ou une liste explicite d'identifiants. */
const isOptionalStoreIds = (value: unknown): boolean =>
  value === undefined ||
  value === 'all' ||
  (Array.isArray(value) && value.every((id) => Number.isInteger(id) && Number(id) > 0))

/** Forme d'un grant v2 : clés exactes et types, sans regarder la taxonomie. */
const isV2GrantShape = (value: unknown): value is CurrentAdminRbacV2Grant => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const grant = value as Record<string, unknown>
  // Les conditions RH de l'ancien modèle n'ont plus de clé : `hasExactKeys` les refuse avec le
  // reste des clés inconnues, sans jamais les réinterpréter en population.
  return (
    hasExactKeys(
      grant,
      ['resource', 'action'],
      ['scope', 'roleId', 'permissionId', 'storeIds', 'population', 'adminRoleId'],
    ) &&
    typeof grant.resource === 'string' &&
    typeof grant.action === 'string' &&
    (grant.scope === undefined || typeof grant.scope === 'string') &&
    isOptionalPositiveInteger(grant.roleId) &&
    isOptionalPositiveInteger(grant.permissionId) &&
    isOptionalPositiveInteger(grant.adminRoleId) &&
    isOptionalStoreIds(grant.storeIds)
  )
}

/** Un grant v2 est connu quand la taxonomie porte son couple, son périmètre et sa population. */
const isKnownV2Grant = (grant: CurrentAdminRbacV2Grant): boolean =>
  isRbacV2ActionAllowed(grant.resource, grant.action) &&
  isRbacV2ScopeAllowed(grant.resource, grant.action, grant.scope ?? 'global') &&
  parseHrPopulation(grant.population, grant.resource as RbacV2Resource).ok

/**
 * Parse le DTO de session BO avant toute installation d'ability.
 *
 * Strict sur la **structure** (clés exactes, types) : une réponse mal formée rend `null` et le BO
 * reste sur son dernier contrat exploitable. Tolérant sur le **catalogue** : un grant dont la
 * ressource, l'action, le périmètre ou la population n'existent pas (ligne `role_permissions`
 * survivant à une ressource retirée du code, BO déployé avant le backend) est simplement écarté —
 * il ne donne aucun droit — et le reste du contrat reste valide. Rejeter tout le contrat pour un
 * grant inconnu faisait retomber le BO sur le régime hérité, permissif côté v2 (staging, 26/09/2026).
 *
 * Sans grant écarté, la réponse est renvoyée telle quelle : son identité suit celle de la donnée
 * React Query, ce qui garde stable la mémorisation du contrat dans `GlobalProvider`.
 */
export const parseCurrentAdminRbacPermissionsResponse = (
  value: unknown,
): CurrentAdminRbacPermissionsResponse | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const response = value as Record<string, unknown>
  if (
    !hasExactKeys(response, [
      'schemaVersion',
      'authorizationModel',
      'enforcementEnabled',
      'grants',
      'permissionV2Enabled',
      'grantsV2',
    ]) ||
    response.schemaVersion !== 2 ||
    response.authorizationModel !== 'rbac-v2' ||
    typeof response.enforcementEnabled !== 'boolean' ||
    typeof response.permissionV2Enabled !== 'boolean' ||
    !Array.isArray(response.grants) ||
    !response.grants.every(isLegacyGrantShape) ||
    !Array.isArray(response.grantsV2) ||
    !response.grantsV2.every(isV2GrantShape)
  ) {
    return null
  }
  const contract = response as unknown as CurrentAdminRbacPermissionsResponse
  const grants = contract.grants.filter(isKnownRbacV1Grant)
  const grantsV2 = contract.grantsV2.filter(isKnownV2Grant)
  if (grants.length === contract.grants.length && grantsV2.length === contract.grantsV2.length) {
    return contract
  }
  return { ...contract, grants, grantsV2 }
}

export interface RbacRoleAdmin {
  id: number
  email: string
  firstName: string
  lastName: string
}

/**
 * Affectation d'un rôle à un compte BO, avec son périmètre boutique. `allStores: true` implique une
 * liste vide ; `allStores: false` implique au moins une boutique.
 */
export interface RbacRoleAssignment {
  adminId: number
  allStores: boolean
  storeIds: number[]
}

/** Élément du dialogue « Assigner des utilisateurs » d'un rôle (`PUT /roles/:id/admins`). */
export interface RbacRoleAssignmentInput {
  adminId: number
  allStores: boolean
  storeIds: number[]
}

/** Élément de la section « Rôles » d'une fiche compte BO (`PUT /admins/:id/roles`). */
export interface RbacAdminRoleAssignmentInput {
  roleId: number
  allStores: boolean
  storeIds: number[]
}

export interface ReplaceRbacRoleAssignmentsRequest {
  assignments: RbacRoleAssignmentInput[]
}

export interface ReplaceRbacAdminRoleAssignmentsRequest {
  assignments: RbacAdminRoleAssignmentInput[]
}

export interface RbacRole {
  id: number
  name: string
  description: string | null
  /** Famille de rangement (`role_families.key`), `null` = « Sans famille ». Aucun droit conféré. */
  familyKey: string | null
  /**
   * Rôle « Full Access » : toute la taxonomie v2, resynchronisée à chaque déploiement. Le serveur
   * refuse d'en modifier les permissions ; l'éditeur les montre en lecture seule. Absent sur un
   * serveur antérieur, ce qui vaut `false`.
   */
  fullAccess?: boolean
  permissions: RbacRolePermission[]
  /** Permissions v2 (taxonomie `rbac.v2.ts`) — vide tant que le rôle n'a pas de droits v2. */
  permissionsV2: RbacV2RolePermission[]
  admins: RbacRoleAdmin[]
  /** Périmètre de chaque affectation du rôle ; absent tant que le serveur ne le sérialise pas. */
  assignments?: RbacRoleAssignment[]
}

/**
 * `adminIds` a disparu de la création : une liste plate sans périmètre y créait des affectations
 * « toutes boutiques » implicites (audit du 19/09, B4). Une affectation posée à la création porte
 * désormais son périmètre, comme dans le dialogue « Assigner des utilisateurs ».
 */
export interface CreateRbacRoleRequest {
  name: string
  description?: string | null
  permissions?: RbacRolePermission[]
  permissionsV2?: RbacV2RolePermission[]
  assignments?: RbacRoleAssignmentInput[]
}

/**
 * Compte affectable, tel que le rend `GET /rbac/assignable-admins` : le strict nécessaire du
 * dialogue « Assigner des utilisateurs », servi sous `edit roles-admin.roles.admin-assignment`
 * plutôt que sous `read users.bo-users.user` (MAJ-05).
 */
export interface RbacAssignableAdmin {
  id: number
  firstName: string
  lastName: string
  email: string
}

/**
 * Option du sélecteur « développeur » d'un projet foncier (`GET /real-estate/developers`) :
 * l'éligibilité « Prez projet » est calculée côté serveur, et rien d'autre que le nom ne sort
 * (M2 / M3 / TAX-B1).
 */
export interface RealEstateDeveloperOption {
  id: number
  firstName: string
  lastName: string
}

export interface UpdateRbacRoleRequest {
  name?: string
  description?: string | null
  /** Famille de rangement du rôle ; `null` le remet « Sans famille ». */
  familyKey?: string | null
}

export interface ReplaceRbacRolePermissionsRequest {
  permissions: RbacRolePermission[]
}

export interface ReplaceRbacRoleAdminsRequest {
  adminIds: number[]
}

/**
 * Row returned by GET /feature-flags (Lucid JSON: camelCase, ISO date strings).
 * Aligned with `TimestampsAndID` without importing `index.ts` (avoids circular export).
 */
export interface FeatureFlag {
  id: number
  createdAt: string
  updatedAt: string
  name: string
  enabled: boolean
}

/** `feature_flags.name` value; backend RBAC enforcement reads this via FeatureFlagService. */
export const RBAC_ENFORCEMENT_FEATURE_FLAG = 'ENFORCE_RBAC_SYSTEM' as const
